import { ActionType } from './userCommunicationAgent';

export enum StepKind {
  ACTION = 'ACTION',
  CONTAINER = 'CONTAINER',
}

/**
 * What a step leaves under `step.step-N.output` after it succeeds. A failed or
 * skipped step may write '' (or nothing); `kind` describes the success shape.
 */
export enum StepOutputKind {
  NONE = 'NONE',
  SCALAR = 'SCALAR',
  JSON_OBJECT = 'JSON_OBJECT',
  JSON_ARRAY = 'JSON_ARRAY',
  BOOLEAN_STRING = 'BOOLEAN_STRING',
  /** Free text, unless the config carries a valid `outputSchema`: then a JSON object with the schema's properties as keys. */
  SCHEMA_DEFINED = 'SCHEMA_DEFINED',
  /** No bare `.output`; only exact `.output.<key>` context entries. */
  CONTEXT_KEYS = 'CONTEXT_KEYS',
}

export interface StepOutputContract {
  kind: StepOutputKind;
  /** JSON_OBJECT: the JSON keys. JSON_ARRAY / SCALAR / CONTEXT_KEYS: the exact `.output.<key>` context entries. */
  keys?: readonly string[];
  /** JSON_OBJECT: the key whose value is the list a FOR_EACH loops over. */
  arrayKey?: string;
  /** Subset of `keys` absent on the happy path. */
  optionalKeys?: readonly string[];
  /** SCALAR: the closed set of values the step can write. */
  values?: readonly string[];
  /** Other `step.step-N.<key>` entries the step writes beside `.output`. */
  extraContextKeys?: readonly string[];
  /** Subset of `extraContextKeys` not written on the happy path. */
  conditionalContextKeys?: readonly string[];
  /** stepConfig field holding the JSON schema that turns a SCHEMA_DEFINED output structured. */
  outputSchemaField?: string;
  /** The runner, not the strategy, writes `.output` once the paused step resumes. */
  writtenOnResume?: boolean;
}

export interface StepContract {
  kind: StepKind;
  output: StepOutputContract;
  /** stepConfig paths resolved as `{{...}}` templates (`[]` = array element, `*` = every string value). */
  templateFields: readonly string[];
  /** stepConfig paths read as a bare variable path, e.g. `step.step-1.output.id`. */
  barePathFields: readonly string[];
  /** stepConfig paths the runtime never template-resolves: a `{{...}}` there is used as literal text. */
  literalFields?: readonly string[];
  /** stepConfig paths the runtime resolves as one string: an array or object there makes the resolver throw. */
  stringOnlyFields?: readonly string[];
  childOutputsAddressable?: boolean;
  childScopeVariables?: readonly string[];
  runtimeSupported: boolean;
  webSupported: boolean;
}

const NO_OUTPUT = { kind: StepOutputKind.NONE } as const;

const jsonObject = <
  const Keys extends readonly string[],
  const Optional extends readonly string[] = readonly [],
>(
  keys: Keys,
  optionalKeys?: Optional,
) =>
  ({
    kind: StepOutputKind.JSON_OBJECT,
    keys,
    ...(optionalKeys && { optionalKeys }),
  }) as {
    kind: StepOutputKind.JSON_OBJECT;
    keys: Keys;
    optionalKeys: Optional;
  };

const listedIn = <const Output extends { keys: readonly string[] }>(
  output: Output,
  arrayKey: Output['keys'][number],
) => ({ ...output, arrayKey });

const jsonArray = <const Keys extends readonly string[] = readonly []>(
  keys?: Keys,
) =>
  ({
    kind: StepOutputKind.JSON_ARRAY,
    ...(keys && { keys }),
  }) as { kind: StepOutputKind.JSON_ARRAY; keys: Keys };

interface ActionFlags {
  runtimeSupported?: boolean;
  webSupported?: boolean;
}

type FlagValue<Flags extends ActionFlags, Key extends keyof ActionFlags> =
  Flags extends Record<Key, infer Value> ? Value : true;

const action = <
  const Output extends StepOutputContract,
  const Flags extends ActionFlags = Record<never, never>,
>(
  output: Output,
  fields: {
    templateFields?: readonly string[];
    barePathFields?: readonly string[];
    literalFields?: readonly string[];
    stringOnlyFields?: readonly string[];
  } = {},
  flags: Flags = {} as Flags,
): Omit<
  StepContract,
  'kind' | 'output' | 'runtimeSupported' | 'webSupported'
> & {
  kind: StepKind.ACTION;
  output: Output;
  runtimeSupported: FlagValue<Flags, 'runtimeSupported'>;
  webSupported: FlagValue<Flags, 'webSupported'>;
} => ({
  kind: StepKind.ACTION,
  output,
  templateFields: fields.templateFields ?? [],
  barePathFields: fields.barePathFields ?? [],
  literalFields: fields.literalFields ?? [],
  stringOnlyFields: fields.stringOnlyFields ?? [],
  runtimeSupported: (flags.runtimeSupported ?? true) as FlagValue<
    Flags,
    'runtimeSupported'
  >,
  webSupported: (flags.webSupported ?? true) as FlagValue<
    Flags,
    'webSupported'
  >,
});

export const FOR_EACH_ITEM_VARIABLE = 'forEach.item';
export const FOR_EACH_INDEX_VARIABLE = 'forEach.index';
export const FOR_EACH_TOTAL_VARIABLE = 'forEach.total';

const CALENDAR_RESULT_KEYS = ['success', 'eventId'] as const;

/**
 * Single declaration of what each step writes and which config fields resolve
 * variables. Recorded from the strategies themselves; the coverage tests in
 * apps/api/.../utils/stepContracts run the real strategies against it.
 */
export const STEP_CONTRACTS = {
  [ActionType.ARCHIVE]: action(NO_OUTPUT),
  [ActionType.ADD_LABELS]: action(NO_OUTPUT),
  [ActionType.REMOVE_LABELS]: action(NO_OUTPUT),
  [ActionType.FORWARD]: action(NO_OUTPUT, {}, { runtimeSupported: false }),
  [ActionType.DELETE]: action(
    NO_OUTPUT,
    {},
    { runtimeSupported: false, webSupported: false },
  ),
  [ActionType.MARK_READ]: action(NO_OUTPUT),
  [ActionType.MARK_UNREAD]: action(NO_OUTPUT),
  [ActionType.NOTIFY_BY_EMAIL]: action(NO_OUTPUT),
  [ActionType.CHECK_IF]: {
    kind: StepKind.CONTAINER,
    // Plain text: "Condition met — ran N action(s). ..." (nothing is written when conditions[] is empty).
    output: { kind: StepOutputKind.SCALAR },
    templateFields: [],
    barePathFields: ['conditions[].variable'],
    childOutputsAddressable: true,
    runtimeSupported: true,
    webSupported: true,
  },
  [ActionType.AI_ASK]: action(
    {
      kind: StepOutputKind.SCHEMA_DEFINED,
      outputSchemaField: 'outputSchema',
    },
    { templateFields: ['prompt'] },
  ),
  [ActionType.SUMMARIZE]: action(jsonObject(['summary', 'hasSummary']), {
    templateFields: ['prompt'],
  }),
  [ActionType.DECIDE]: action(
    { kind: StepOutputKind.BOOLEAN_STRING },
    { templateFields: ['prompt'] },
  ),
  [ActionType.CHANGE_CATEGORY]: action(NO_OUTPUT),
  [ActionType.DRAFT_EMAIL]: action(NO_OUTPUT, {
    templateFields: ['subject', 'body'],
    barePathFields: ['recipient'],
  }),
  [ActionType.DRAFT_REPLY]: action(NO_OUTPUT, { templateFields: ['body'] }),
  [ActionType.GENERATE_DOBOX]: action(jsonArray(), {
    templateFields: ['prompt'],
  }),
  [ActionType.LOAD_DOBOX]: action(jsonArray()),
  [ActionType.LOAD_COMMUNICATIONS]: action(jsonArray()),
  [ActionType.SEND_NOTIFICATION_EMAIL]: action(NO_OUTPUT, {
    templateFields: ['heading', 'subject', 'description'],
  }),
  [ActionType.SEND_EMAIL]: action(NO_OUTPUT, {
    templateFields: ['subject', 'body'],
    barePathFields: ['recipients[]'],
  }),
  [ActionType.SEND_USER_SLACK_MESSAGE]: action(NO_OUTPUT, {
    templateFields: ['message'],
  }),
  [ActionType.SEND_BOT_SLACK_MESSAGE]: action(NO_OUTPUT, {
    templateFields: ['message'],
  }),
  [ActionType.VALIDATE_DOBOX_RULES]: action(
    {
      kind: StepOutputKind.SCALAR,
      values: ['PROCEED', 'BLOCK'],
      extraContextKeys: ['rules', 'matchType', 'matchedRuleId', 'reason'],
      conditionalContextKeys: ['matchType', 'matchedRuleId', 'reason'],
    },
    { templateFields: ['communicationsInput'] },
  ),
  [ActionType.APPEND_TO_DOBOX]: action(
    {
      kind: StepOutputKind.SCALAR,
      values: ['SUCCESS', 'FAILED'],
      extraContextKeys: ['appendedEntryId', 'reason'],
    },
    {
      templateFields: ['inboxEntryId', 'communicationIds'],
      stringOnlyFields: ['communicationIds'],
    },
  ),
  [ActionType.CHECK_AND_APPEND_DOBOX]: action({
    kind: StepOutputKind.SCALAR,
    values: ['MATCHED', 'NO_MATCH'],
    extraContextKeys: [
      'matchedEntryId',
      'reason',
      'matchedCount',
      'matchedEntryIds',
    ],
    conditionalContextKeys: ['matchedCount', 'matchedEntryIds'],
  }),
  [ActionType.CREATE_DOBOX]: action(
    {
      kind: StepOutputKind.SCALAR,
      // Generated flows read the new id as .output.inboxEntryId, so it is written beside the bare id.
      keys: ['inboxEntryId'],
      extraContextKeys: ['createdEntryId', 'ignoredParticipantIds'],
      conditionalContextKeys: ['ignoredParticipantIds'],
    },
    {
      templateFields: ['subject', 'description', 'taskGroupId'],
      // Linked as message ids as typed; the store aborts the create when one does not exist.
      literalFields: ['communicationIds[]'],
    },
  ),
  [ActionType.RESOLVE_DOBOX]: action(
    { kind: StepOutputKind.SCALAR, extraContextKeys: ['resolvedEntryId'] },
    {
      templateFields: ['inboxEntryId'],
    },
  ),
  [ActionType.ACCEPT_DOBOX_SUGGESTION]: action(
    { kind: StepOutputKind.SCALAR, extraContextKeys: ['acceptedEntryId'] },
    { templateFields: ['inboxEntryId'] },
  ),
  [ActionType.ADD_DOBOX_COMMENT]: action(
    { kind: StepOutputKind.SCALAR, extraContextKeys: ['commentedEntryId'] },
    {
      templateFields: ['inboxEntryId', 'comment'],
    },
  ),
  [ActionType.UPDATE_DOBOX_PARTICIPANTS]: action(
    { kind: StepOutputKind.SCALAR, extraContextKeys: ['updatedEntryId'] },
    { templateFields: ['inboxEntryId'] },
  ),
  [ActionType.UPDATE_DOBOX_LABELS]: action(
    { kind: StepOutputKind.SCALAR, extraContextKeys: ['updatedEntryId'] },
    {
      templateFields: ['inboxEntryId'],
    },
  ),
  [ActionType.PROCESS_DOBOX]: action(
    jsonObject([
      'created',
      'appended',
      'resolved',
      'reopened',
      'looksResolved',
      'looksResolvedWithdrawn',
    ]),
  ),
  [ActionType.FOR_EACH]: {
    kind: StepKind.CONTAINER,
    output: jsonObject(['iterations']),
    // A bare path here is NOT resolved: it stays literal and is split on commas.
    templateFields: ['sourceArray'],
    barePathFields: [],
    childOutputsAddressable: false,
    childScopeVariables: [
      FOR_EACH_ITEM_VARIABLE,
      FOR_EACH_INDEX_VARIABLE,
      FOR_EACH_TOTAL_VARIABLE,
    ],
    runtimeSupported: true,
    webSupported: true,
  },
  [ActionType.SEND_USER_GOOGLE_CHAT_MESSAGE]: action(NO_OUTPUT, {
    templateFields: ['message', 'spaceName', 'threadName'],
  }),
  [ActionType.MCP_TOOL_CALL]: action(jsonArray(), {
    templateFields: ['arguments.*'],
  }),
  [ActionType.CALENDAR_RSVP]: action(
    jsonObject([...CALENDAR_RESULT_KEYS, 'responseStatus']),
    { templateFields: ['responseStatus'], barePathFields: ['eventId'] },
  ),
  [ActionType.CALENDAR_CREATE_EVENT]: action(
    jsonObject([...CALENDAR_RESULT_KEYS, 'meetLink'], ['meetLink']),
    {
      templateFields: [
        'summary',
        'description',
        'startDateTime',
        'endDateTime',
        'location',
      ],
      barePathFields: ['attendees[]'],
    },
  ),
  [ActionType.CALENDAR_DELETE_EVENT]: action(jsonObject(CALENDAR_RESULT_KEYS), {
    barePathFields: ['eventId'],
  }),
  [ActionType.CALENDAR_RESCHEDULE_EVENT]: action(
    jsonObject(CALENDAR_RESULT_KEYS),
    {
      templateFields: ['startDateTime', 'endDateTime'],
      barePathFields: ['eventId'],
    },
  ),
  [ActionType.CALENDAR_ADD_ATTENDEES]: action(
    jsonObject(CALENDAR_RESULT_KEYS),
    {
      barePathFields: ['eventId', 'attendees[]'],
    },
  ),
  [ActionType.CALENDAR_REMOVE_ATTENDEES]: action(
    jsonObject(CALENDAR_RESULT_KEYS),
    { barePathFields: ['eventId', 'attendees[]'] },
  ),
  [ActionType.AGENT_TASK]: action(
    {
      kind: StepOutputKind.SCHEMA_DEFINED,
      outputSchemaField: 'outputSchema',
    },
    { templateFields: ['goal'] },
  ),
  [ActionType.LOAD_MEETINGS]: action(jsonArray()),
  [ActionType.SEND_INSTAGRAM_MESSAGE]: action(
    jsonObject(['messageId', 'sentAt']),
    { templateFields: ['igAccountId', 'conversationId', 'message'] },
  ),
  [ActionType.REPLY_INSTAGRAM_COMMENT]: action(
    jsonObject(['commentId', 'repliedAt']),
    { templateFields: ['igAccountId', 'commentId', 'message'] },
  ),
  [ActionType.PUBLISH_INSTAGRAM_POST]: action(
    jsonObject(['mediaId', 'permalink', 'publishedAt']),
    {
      templateFields: [
        'igAccountId',
        'kind',
        'caption',
        'imageUrl',
        'generatedImageId',
        'videoUrl',
        'coverUrl',
        'children[].imageUrl',
        'children[].generatedImageId',
      ],
    },
  ),
  [ActionType.SEND_WHATSAPP_MESSAGE]: action(
    jsonObject(
      ['success', 'wamid', 'sentAt', 'reason', 'error'],
      ['wamid', 'sentAt', 'reason', 'error'],
    ),
    {
      templateFields: [
        'phoneNumberId',
        'to',
        'message',
        'replyToMessageId',
        'templateBodyParameters[]',
      ],
    },
  ),
  [ActionType.NOTIFY_ASSISTANT]: action(NO_OUTPUT, {
    templateFields: ['messageTemplate'],
  }),
  [ActionType.ASK_USER]: action(
    {
      kind: StepOutputKind.SCALAR,
      keys: ['decision'],
      writtenOnResume: true,
    },
    { templateFields: ['messageTemplate'] },
  ),
  [ActionType.RUN_ASSISTANT]: action(NO_OUTPUT, {
    templateFields: ['promptTemplate'],
  }),
  [ActionType.LOAD_TEAM_MEMBERS]: action(
    listedIn(jsonObject(['members']), 'members'),
    {
      templateFields: ['teamId'],
    },
  ),
  [ActionType.CHECK_TEAM_CALENDAR]: action(
    jsonObject(
      ['mode', 'slots', 'events', 'membersChecked', 'skipped', 'error'],
      ['slots', 'events', 'error'],
    ),
    {
      templateFields: [
        'members',
        'timeMin',
        'timeMax',
        'range',
        'subjectContains',
        'durationMinutes',
        'maxSlots',
        'includeSelf',
      ],
    },
  ),
  [ActionType.LOAD_KNOWLEDGE_PAGE]: action({ kind: StepOutputKind.SCALAR }),
  [ActionType.APPROVAL_GATE]: action(
    {
      kind: StepOutputKind.SCALAR,
      values: ['APPROVED', 'REJECTED'],
      keys: ['decision'],
      writtenOnResume: true,
    },
    { templateFields: ['summaryPrompt'] },
  ),
  [ActionType.CLASSIFY_ACTIONABILITY]: action(
    {
      kind: StepOutputKind.SCALAR,
      values: ['ACTIONABLE', 'NOT_ACTIONABLE'],
      extraContextKeys: ['score', 'reason'],
    },
    { templateFields: ['communicationsInput'] },
  ),
  [ActionType.SEARCH_PEOPLE]: action(jsonArray(['count']), {
    templateFields: ['name', 'email', 'jobTitle', 'teamId'],
  }),
  [ActionType.UPDATE_PERSON_NOTES]: action(
    {
      kind: StepOutputKind.SCALAR,
      extraContextKeys: ['__updateFailed'],
      conditionalContextKeys: ['__updateFailed'],
    },
    { templateFields: ['personId', 'notesTemplate', 'teamId'] },
  ),
  [ActionType.LOAD_TEAM_PEOPLE]: action(jsonArray(['count']), {
    templateFields: ['teamId', 'query'],
  }),
  [ActionType.CREATE_PERSON_EVENT]: action(
    {
      kind: StepOutputKind.SCALAR,
      extraContextKeys: ['__updateFailed'],
      conditionalContextKeys: ['__updateFailed'],
    },
    {
      templateFields: [
        'personId',
        'personEmail',
        'titleTemplate',
        'descriptionTemplate',
        'teamId',
      ],
    },
  ),
  [ActionType.GBP_REPLY_REVIEW]: action(
    jsonObject(['success', 'reviewName', 'isUpdate']),
    { templateFields: ['reviewName', 'replyText'] },
  ),
  [ActionType.PUBLISH_FACEBOOK_POST]: action(
    jsonObject(['postId', 'permalink', 'scheduled', 'pageId'], ['permalink']),
    {
      templateFields: ['pageId', 'scheduledAt', 'message', 'imageUrl', 'link'],
    },
  ),
  [ActionType.REPLY_FACEBOOK_COMMENT]: action(
    jsonObject(['replyId', 'commentId', 'pageId', 'private']),
    { templateFields: ['message', 'commentId', 'pageId'] },
  ),
  [ActionType.LOAD_FACEBOOK_POSTS]: action(
    listedIn(
      jsonObject(['posts', 'nextCursor', 'pageId'], ['nextCursor']),
      'posts',
    ),
    { templateFields: ['pageId'] },
  ),
  [ActionType.LOAD_FACEBOOK_COMMENTS]: action(
    listedIn(
      jsonObject(
        ['comments', 'nextCursor', 'postId', 'pageId'],
        ['nextCursor'],
      ),
      'comments',
    ),
    { templateFields: ['postId', 'pageId'] },
  ),
  [ActionType.SEND_FACEBOOK_MESSAGE]: action(
    jsonObject(
      ['messageId', 'recipientId', 'pageId', 'refused', 'reason', 'message'],
      ['messageId', 'refused', 'reason', 'message'],
    ),
    { templateFields: ['message', 'recipientId', 'pageId'] },
  ),
  [ActionType.SEND_TEAMS_MESSAGE]: action(
    {
      kind: StepOutputKind.CONTEXT_KEYS,
      keys: ['messageId', 'webUrl'],
      optionalKeys: ['webUrl'],
    },
    { templateFields: ['chatId', 'message'] },
  ),
  [ActionType.LOAD_CHAT_COMMUNICATIONS]: action(jsonArray(['count']), {
    // Which of these apply depends on config.platform.
    templateFields: [
      'channelId',
      'pageId',
      'conversationId',
      'igAccountId',
      'chatId',
      'emailAddress',
      'spaceName',
      'phoneNumberId',
    ],
  }),
} satisfies Record<ActionType, StepContract>;

type ContractOf<Type extends ActionType> = (typeof STEP_CONTRACTS)[Type];

type ActionTypesWhere<Match> = {
  [Type in ActionType]: ContractOf<Type> extends Match ? Type : never;
}[ActionType];

/** Has a strategy in `strategiesMap`; containers (CHECK_IF, FOR_EACH) are dispatched outside it. */
export type RuntimeSupportedActionType = ActionTypesWhere<{
  kind: StepKind.ACTION;
  runtimeSupported: true;
}>;

/** Safe for the flow generator to emit: runs in the runtime AND opens in the web editor. */
export type GeneratableActionType = ActionTypesWhere<{
  runtimeSupported: true;
  webSupported: true;
}>;

export const GENERATABLE_ACTION_TYPES: ReadonlySet<ActionType> = new Set(
  (Object.keys(STEP_CONTRACTS) as ActionType[]).filter(
    (type) =>
      STEP_CONTRACTS[type].runtimeSupported &&
      STEP_CONTRACTS[type].webSupported,
  ),
);

type OutputOf<Type extends ActionType> = ContractOf<Type>['output'];

/** Runtime-supported steps, containers included, that write `.output` (or `.output.<key>` entries) on success. */
export type OutputWritingActionType = {
  [Type in ActionTypesWhere<{
    runtimeSupported: true;
  }>]: OutputOf<Type> extends {
    kind: StepOutputKind.NONE;
  }
    ? never
    : Type;
}[ActionTypesWhere<{ runtimeSupported: true }>];

/** The `.output.<key>` context entries a step declares; `never` when it declares none. */
export type StepOutputKey<Type extends ActionType> =
  OutputOf<Type> extends {
    keys: readonly (infer Key extends string)[];
  }
    ? Key
    : never;
